import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { KeyRound, Mail, WifiOff } from 'lucide-react';
import { ApiClientError } from '@mouldtrack/shared';
import { login } from '../../api/client.js';
import { setSession } from '../../auth/session.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { Button } from '../../components/ui/button.js';
import { Field, Input } from '../../components/ui/field.js';
import { useOnlineStatus } from '../../sync/useSync.js';

/**
 * The only screen that genuinely requires a network.
 *
 * Everything past this point runs from IndexedDB, so we say plainly that the
 * first sign-in needs signal rather than letting someone retry a dead button in
 * a store room.
 */
export function LoginPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const online = useOnlineStatus();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = 'Sign in · MouldTrack';
  }, []);

  if (user) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from && from !== '/login' ? from : '/'} replace />;
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const tokens = await login({ email: email.trim(), password });
      await setSession({
        user: tokens.user,
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        verifiedAt: new Date().toISOString(),
      });
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.status === 401 ? 'Incorrect email or password.' : err.message);
      } else {
        setError(
          online
            ? 'Could not reach the server. Check the connection and try again.'
            : 'You are offline. The first sign-in on this device needs a connection.',
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="grid min-h-dvh place-items-center bg-background px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-7 flex flex-col items-center text-center">
          <img src="/icon.svg" alt="" className="mb-3 size-13 rounded-2xl shadow-sm" />
          <h1 className="text-xl font-semibold tracking-tight text-foreground">MouldTrack</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Mould catalog, nameplates and location tracking
          </p>
        </div>

        {!online ? (
          <div className="mb-4 flex items-start gap-2.5 rounded-lg border border-warning/30 bg-warning-subtle px-3 py-2.5 text-sm text-warning">
            <WifiOff className="mt-0.5 size-4 shrink-0" aria-hidden />
            <p>
              You are offline. Signing in for the first time on this device needs a connection -
              after that, MouldTrack works without one.
            </p>
          </div>
        ) : null}

        <form
          onSubmit={onSubmit}
          className="flex flex-col gap-4 rounded-xl border border-border bg-surface p-5 shadow-sm"
        >
          <Field label="Email">
            <div className="relative">
              <Mail
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@jayprecision.com"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                required
                className="pl-9"
              />
            </div>
          </Field>

          <Field label="Password">
            <div className="relative">
              <KeyRound
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
                className="pl-9"
              />
            </div>
          </Field>

          {error ? (
            <p role="alert" className="rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger">
              {error}
            </p>
          ) : null}

          <Button type="submit" variant="primary" size="lg" loading={submitting} className="mt-1">
            Sign in
          </Button>
        </form>

        <p className="mt-5 text-center text-xs text-subtle-foreground">
          Mindgrid Labs · Jay Precision Products India Pvt. Ltd.
        </p>
      </div>
    </div>
  );
}
